(ns frontend.view.settings2.metabase-dashboards.view
  (:require
   [basic-components.html-table.cell :as table-cell]
   [basic-components.html-table.table :as table]
   [clojure.data :as data]
   [clojure.set :as set]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [frontend.actions.entity :as entity-actions]
   [frontend.logging :as logging]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.view.modals.entity-editor.view :as entity-editor-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.settings2.metabase-dashboards.db :as db]
   [frontend.view.user-management.components :as components]
   [reagent.core :as r]
   [ui.components.breadcrumbs.view :as breadcrumbs]
   [ui.components.button.view :as button]
   [ui.components.input.checkbox :as checkbox]
   [ui.components.radix-ui.popover.view :as popover]))

(defn breadcrumbs
  []
  [breadcrumbs/container
   [breadcrumbs/item
    {:on-click (fn [e]
                 (routing/set-view! [:account-settings :tab-key "config"]))}
    (loc/tr :settings/account-settings)]
   [breadcrumbs/item
    {:active? true}
    (loc/tr :settings/metabase-dashboards)]])

(defn members-renderer
  [arg]
  (let [members (:cell-value arg)
        member-count (count members)]
    (cond
      (zero? member-count)
      "-"
      (> member-count 5)
      (str (->> members
                (take 3)
                (map (fn [id]
                       (entity/display-string (session/get-entity id))))
                (string/join ", "))
           " and "
           (- member-count 3)
           " other members")
      :else
      (->> members
           (map (fn [id]
                  (entity/display-string (session/get-entity id))))
           (sort)
           (string/join ", ")))))

(defn teams-renderer
  [arg]
  (let [teams (:cell-value arg)
        teams-count (count teams)]
    (cond
      (zero? teams-count)
      "-"
      (> teams-count 5)
      (str (->> teams
                (take 3)
                (map (fn [id]
                       (entity/display-string (session/get-entity id))))
                (string/join ", "))
           " and "
           (- teams-count 3)
           " other members")
      :else
      (->> teams
           (map (fn [id]
                  (entity/display-string (session/get-entity id))))
           (sort)
           (string/join ", ")))))

(defn attach-scroll-handler-to-parents
  [node handler]
  (when node
    (.addEventListener node "scroll" handler)
    (recur (-> node
               .-parentElement)
           handler)))

(defn remove-scroll-handler-to-parents
  [node handler]
  (when node
    (.removeEventListener node "scroll" handler)
    (recur (-> node
               .-parentElement)
           handler)))

(defn edit-metabase-dashboard
  [dashboard-id]
  [:span
   {:class "min-w-48 mb-0 p-2 flex items-center gap-2
                                   text-sm text-base-light-text
                                   hover:bg-base-regular !outline-0 shadow-none
                                   rounded
                                   cursor-pointer"
    :on-click
    (fn [_]
      (let [state (r/atom (session/get-entity dashboard-id))]
        (modals/open-modal!
         entity-editor-modal/modal-key
         {:entity-id               dashboard-id
          :entity-state            state
          :on-ok!                  (fn [entity-id close!]
                                     (let [dashboard-keys [:metabase-dashboard/label
                                                           :metabase-dashboard/members
                                                           :metabase-dashboard/teams]
                                           current-vals (select-keys (get @session/entities entity-id)
                                                                     dashboard-keys)
                                           new-vals (select-keys @state dashboard-keys)
                                           [removed added common] (data/diff current-vals new-vals)
                                           updates (when (or removed added)
                                                     (merge added
                                                            {:metabase-dashboard/teams   (set/union (set (:metabase-dashboard/teams common))
                                                                                                    (set (:metabase-dashboard/teams added)))
                                                             :metabase-dashboard/members (set/union (set (:metabase-dashboard/members common))
                                                                                                    (set (:metabase-dashboard/members added)))}))
                                           no-updates (empty? updates)]
                                       (if no-updates
                                         (close!)
                                         (entity-actions/*->entity-update!
                                          entity-id
                                          updates
                                          close!
                                          (fn [err]
                                            (logging/error "Failed to update metabase dashboard" err))))))
          :no-changes?             (fn []
                                     (let [dashboard-keys [:metabase-dashboard/label
                                                           :metabase-dashboard/members
                                                           :metabase-dashboard/teams]
                                           current-vals (select-keys (get @session/entities dashboard-id)
                                                                     dashboard-keys)
                                           new-vals (select-keys @state dashboard-keys)
                                           [removed added common] (data/diff current-vals new-vals)]
                                       (not (or removed added))))
          :skip-success-msg?       true
          :prevent-backend-update? true
          :title-text              (loc/tr :edit-metabase-dashboard)})))}
   [:i.fal.fa-pen]
   (loc/tr :edit-metabase-dashboard)])

(defn delete-metabase-dashboard
  [dashboard-id]
  [:div
   {:class "min-w-48 mb-0 py-2 *:py-0 *:font-normal flex items-center gap-3
                                   text-sm *:text-sm text-base-light-text
                                   hover:bg-base-regular !outline-0 shadow-none
                                   rounded
                                   cursor-pointer"}
   [button/secondary-open
    {:disabled? true
     :tooltip   (loc/tr :cannot-delete-metabase-dashboards)}
    [:span
     [:i.fal.fa-trash]
     (loc/tr :delete-metabase-dashboard)]]])

(defn tools
  [row-data]
  (let [metabase-dashboard-id (:db/id row-data)]
    [:div
     {:class "flex flex-col gap-1 bg-base-light *:text-base-light-text rounded"}
     [edit-metabase-dashboard metabase-dashboard-id]
     [delete-metabase-dashboard metabase-dashboard-id]]))

(defn checkbox-cell-renderer
  [{:keys [column-meta cell-value row-data state]}]
  (let [ref (atom nil)
        local-state (r/atom {})
        scroll-handler (fn []
                         (swap! local-state assoc :selected? false))
        init! (fn [dom-node]
                (let [existing-node @ref]
                  (when (and existing-node
                             (not= existing-node dom-node))
                    (remove-scroll-handler-to-parents dom-node scroll-handler))
                  (when (not= existing-node dom-node)
                    (attach-scroll-handler-to-parents dom-node scroll-handler)
                    (reset! ref dom-node))))]
    (r/create-class
     {:component-will-unmount
      (fn [_]
        (when-let [dom-node @ref]
          (remove-scroll-handler-to-parents dom-node scroll-handler)))
      :reagent-render
      (fn [{:keys [column-meta cell-value row-data state]}]
        (fn [{:keys [column-meta cell-value row-data state]}]
          (let [role-id (:role/id row-data)
                role (get @session/entities role-id)
                account-id (:role/subject role)
                account (get @session/entities account-id)
                selected? (boolean (:selected? @local-state))]
            [:div
             {:class "flex gap-2"
              :ref   (fn [r]
                       (when r
                         (init! r)))}

             [popover/view
              {:open?          (:selected? @local-state)
               :on-open-change #(reset! local-state {:selected? %})
               :as-child?      true
               :positions      ["right"]}
              [checkbox/view
               {:value     selected?
                :on-change (fn [e checked?]
                             (swap! local-state assoc :selected? (not selected?)))}]
              [:div
               [tools row-data]]
              ]
             [table-cell/cell
              {:column-meta   column-meta
               :cell-value    cell-value
               :tenant-schema @session/tenant-schema
               :row-data      row-data
               :state         state}]])))})))

(defn resolve-columns
  []
  (concat
   [{:column/key           :metabase-dashboard/label
     :column/label         (loc/tr :metabase-dashboard/label)
     :column/data-type     :data.type/text-simple
     :column/cell-renderer (partial checkbox-cell-renderer)
     :column/class-name    "sticky-col"
     :column/group         0}
    {:column/key           :metabase-dashboard/teams
     :column/label         (loc/tr :metabase-dashboard/teams)
     :column/cell-renderer teams-renderer
     :column/group         1}
    {:column/key           :metabase-dashboard/members
     :column/label         (loc/tr :metabase-dashboard/members)
     :column/cell-renderer members-renderer
     :column/group         2}]))

(defn resolve-row-data
  [metabase-dashboard selected?]
  (merge metabase-dashboard
         {:row-selected? selected?}))

(defn dashboards-table
  [state]
  (let [resolved-dashboards (->> @session/metabase-dashboards
                                 (sort-by :common/ord)
                                 (map resolve-row-data)
                                 (seq))]
    (if-not resolved-dashboards
      [components/not-found-view]
      [:div
       [:div
        {:class "pb-4 text-xl font-semibold"}
        [:span (loc/tr :settings/metabase-dashboards-notification)]]
       [:div
        [:div
         {:class "border border-solid border-base-light-border-paler rounded-lg overflow-x-auto"}
         ^{:key (->> resolved-dashboards
                     (map :db/id)
                     (string/join "-"))}
         [table/view
          resolved-dashboards
          {:id-key             :db/id
           :table-key          "metabase-dashboards"
           :columns            (resolve-columns)
           :state              state
           :use-external-sort? true
           :settings           {:show-selection?  true
                                :show-filters?    true
                                :show-grouping?   false
                                :show-sorting?    true
                                :default-sort-key :common/created-on
                                :column-order     [:db/id :entity/type :common/created-on]}}]]]])))

(defn component
  []
  (let []
    (fn []
      [:<>
       [zap-toolbar/view db/state breadcrumbs nil nil]
       [:div
        {:class "h-[calc(100vh_-_52px-_37px)] overflow-auto"}
        [:div
         {:class "px-4 py-6 lg:mx-auto lg:w-5xl"}
         [dashboards-table db/state]]]])))

(defmethod routing/render-view :settings/metabase-dashboards
  [_]
  [default-page-layout/view
   [component]])
